import { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { Icon } from '../components/SvgIcons';
import './SuperAdminFinalizedPapers.css';

const PAGE_SIZE = 12;

const LEVELS = [
  { value: 'all', label: 'All' },
  { value: 'university', label: 'COE' },
  { value: 'college', label: 'Moderator' },
];

const finalizerLabel = (level) => (level === 'university' ? 'COE' : 'Moderator');

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date)) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function SuperAdminFinalizedPapers() {
  const { showToast } = useToast();

  const [level, setLevel] = useState('all');
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [tenants, setTenants] = useState([]);
  const [page, setPage] = useState(1);

  // Sets modal
  const [viewing, setViewing] = useState(null);
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState({});
  const [showEncryptionModal, setShowEncryptionModal] = useState(false);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [pendingDownloadData, setPendingDownloadData] = useState(null);
  const [showDownloadProgressModal, setShowDownloadProgressModal] = useState(false);

  const fetchPapers = useCallback(async (lvl) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/superadmin/finalized-papers?level=${lvl}`);
      setPapers(data.papers || []);
      setPage(1);
    } catch (err) {
      showToast('Failed to load finalized papers', 'error');
      setPapers([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchPapers(level); }, [level, fetchPapers]);

  useEffect(() => {
    API.get('/superadmin/tenants')
      .then(({ data }) => setTenants(data.tenants || []))
      .catch(() => setTenants([]));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return papers.filter(p => {
      if (tenantFilter !== 'all' && p.tenant_slug !== tenantFilter) return false;
      if (!q) return true;
      return (
        (p.title || '').toLowerCase().includes(q) ||
        (p.owner || '').toLowerCase().includes(q) ||
        (p.finalized_by_name || '').toLowerCase().includes(q) ||
        (p.tenant_name || '').toLowerCase().includes(q) ||
        (p.subject_name || '').toLowerCase().includes(q)
      );
    });
  }, [papers, search, tenantFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const openSets = async (paper) => {
    setViewing(paper);
    setSets([]);
    setSetsLoading(true);
    try {
      const { data } = await API.get(
        `/superadmin/finalized-papers/${paper.tenant_slug}/${paper.level}/${paper.id}/sets`
      );
      setSets(data.sets || []);
    } catch (err) {
      showToast('Failed to load sets', 'error');
      setSets([]);
    } finally {
      setSetsLoading(false);
    }
  };

  const closeModal = () => { setViewing(null); setSets([]); };

  const handleDownloadPDF = async (paperId, paperTitle, customContent = null, password = null) => {
    if (!viewing) return;
    try {
      setDownloadingPDF(prev => ({ ...prev, [paperId]: true }));

      const response = await API.post(
        `/papers/moderator/paper/${paperId}/download-pdf-ai`,
        {
          header: customContent?.header,
          instructions: customContent?.instructions,
          footer: customContent?.footer,
          questions: customContent?.questions,
          password: password || undefined,
        },
        {
          headers: { 'X-Tenant-ID': viewing.tenant_slug },
          responseType: 'blob',
          timeout: 90000,
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = password
        ? `${paperTitle.replace(/[^a-z0-9]/gi, '_')}_AI_Encrypted.pdf`
        : `${paperTitle.replace(/[^a-z0-9]/gi, '_')}_AI.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast(password ? 'Encrypted AI-formatted PDF downloaded' : 'AI-formatted PDF downloaded', 'success');
      setShowEncryptionModal(false);
      setShowDownloadProgressModal(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to download PDF', 'error');
    } finally {
      setDownloadingPDF(prev => ({ ...prev, [paperId]: false }));
      setShowDownloadProgressModal(false);
    }
  };

  const handleDownloadClick = (paperId, paperTitle, customContent) => {
    setPendingDownloadData({ paperId, paperTitle, customContent });
    setShowEncryptionModal(true);
    setEncryptionPassword('');
  };

  const confirmDownloadWithEncryption = async () => {
    if (!pendingDownloadData) return;
    if (encryptionPassword && encryptionPassword.trim().length > 0 && encryptionPassword.trim().length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }
    const { paperId, paperTitle, customContent } = pendingDownloadData;
    setShowEncryptionModal(false);
    setShowDownloadProgressModal(true);
    await handleDownloadPDF(paperId, paperTitle, customContent, encryptionPassword || null);
    setShowDownloadProgressModal(false);
    setPendingDownloadData(null);
    setEncryptionPassword('');
  };

  const downloadSets = (set = null) => {
    if (!viewing) return;
    if (set) {
      const customContent = { header: '', instructions: '', footer: '', questions: set.questions || [] };
      handleDownloadClick(set.id, `${viewing.title}_${set.set_label || 'set'}`, customContent);
    } else {
      if (sets.length === 0) {
        showToast('No questions available to download', 'error');
        return;
      }
      showToast('Please download sets individually by clicking each set\'s download button', 'info');
    }
  };

  return (
    <div className="sfp-page">
      <div className="sfp-header">
        <h1>Finalized Papers</h1>
        <p>Question papers finalized by COE and moderators across all tenants.</p>
      </div>

      <div className="sfp-toolbar">
        <div className="sfp-segment">
          {LEVELS.map(l => (
            <button
              key={l.value}
              className={`sfp-seg-btn ${level === l.value ? 'sfp-seg-btn--active' : ''}`}
              onClick={() => setLevel(l.value)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="sfp-toolbar-right">
          <select
            className="sfp-tenant-select"
            value={tenantFilter}
            onChange={(e) => { setTenantFilter(e.target.value); setPage(1); }}
            title="Filter by tenant"
          >
            <option value="all">All Tenants</option>
            {tenants.map(t => (
              <option key={t.slug} value={t.slug}>{t.university_name || t.slug}</option>
            ))}
          </select>
          <input
            className="sfp-search"
            type="text"
            placeholder="Search title, subject, finalized by or tenant…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <div className="sfp-card">
        {loading ? (
          <div className="sfp-spin-wrap"><div className="sfp-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="sfp-empty">
            <div className="sfp-empty-icon"><Icon name="FileText" size={42} /></div>
            <h3 style={{ margin: '0 0 0.3rem' }}>No finalized papers found</h3>
            <p style={{ margin: 0 }}>
              {search || tenantFilter !== 'all' ? 'Try a different search term or tenant filter.' : 'Finalized papers will appear here once COE or moderators finalize them.'}
            </p>
          </div>
        ) : (
          <>
            <div className="sfp-table-wrap">
              <table className="sfp-table">
                <thead>
                  <tr>
                    <th>Paper Title</th>
                    <th>Finalized By Role</th>
                    <th>Finalized By</th>
                    <th>University / College</th>
                    <th>Subject</th>
                    <th>Tenant</th>
                    <th>Marks</th>
                    <th>Sets</th>
                    <th>Finalized</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(p => (
                    <tr key={`${p.level}-${p.tenant_slug}-${p.id}`}>
                      <td className="sfp-title-cell">{p.title}</td>
                      <td>
                        <span className={`sfp-badge sfp-badge--${p.level}`}>
                          <Icon name={p.level === 'university' ? 'Globe' : 'Building'} size={12} />
                          {finalizerLabel(p.level)}
                        </span>
                      </td>
                      <td>{p.finalized_by_name || '—'}</td>
                      <td>{p.owner || '—'}</td>
                      <td className="sfp-muted">{p.subject_name || '—'}</td>
                      <td className="sfp-muted">{p.tenant_name}</td>
                      <td>{p.total_marks}</td>
                      <td><span className="sfp-sets-pill">{p.sets_count}</span></td>
                      <td className="sfp-muted">{fmtDate(p.finalized_at)}</td>
                      <td>
                        <button
                          className="sfp-view-btn"
                          onClick={() => openSets(p)}
                          disabled={!p.sets_count}
                          title={p.sets_count ? 'View the sets in this paper' : 'No sets available'}
                        >
                          <Icon name="Eye" size={14} /> View Sets
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="sfp-pagination">
                <span className="sfp-pg-info">
                  Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="sfp-pg-btns">
                  <button className="sfp-pg-btn" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>Previous</button>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{safePage} / {totalPages}</span>
                  <button className="sfp-pg-btn" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sets modal */}
      {viewing && (
        <div className="sfp-overlay" onClick={closeModal}>
          <div className="sfp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sfp-modal-head">
              <div>
                <h3>Sets · {viewing.title}</h3>
                <p>
                  <span className={`sfp-badge sfp-badge--${viewing.level}`} style={{ marginRight: '0.5rem' }}>{finalizerLabel(viewing.level)}</span>
                  {viewing.finalized_by_name ? `Finalized by ${viewing.finalized_by_name} · ` : ''}{viewing.owner} · {viewing.tenant_name}
                </p>
              </div>
              <div className="sfp-modal-actions">
                <button
                  className="sfp-dl-btn"
                  onClick={() => downloadSets()}
                  disabled={downloadingPDF[viewing.id] || setsLoading || sets.length === 0}
                  title={sets.length === 0 ? 'No sets to download' : 'Download all sets as AI-enhanced PDF'}
                >
                  <Icon name="Download" size={15} /> {downloadingPDF[viewing.id] ? 'AI Formatting…' : 'AI Enhanced PDF'}
                </button>
                <button className="sfp-modal-close" onClick={closeModal}>{'×'}</button>
              </div>
            </div>
            <div className="sfp-modal-body">
              {setsLoading ? (
                <div className="sfp-spin-wrap"><div className="sfp-spinner" /></div>
              ) : sets.length === 0 ? (
                <div className="sfp-q-empty">No sets found for this paper.</div>
              ) : (
                sets.map((set, si) => (
                  <div className="sfp-set" key={si}>
                    <div className="sfp-set-head">
                      <span className="sfp-set-name">
                        <Icon name="Clipboard" size={15} /> {set.title || set.set_label || `Set ${si + 1}`}
                      </span>
                      <div className="sfp-set-head-right">
                        <span className="sfp-set-meta">
                          {set.questions?.length || 0} question{(set.questions?.length || 0) === 1 ? '' : 's'} · {set.total_marks} marks
                        </span>
                        <button
                          className="sfp-set-dl"
                          onClick={() => downloadSets(set)}
                          disabled={downloadingPDF[set?.id] || downloadingPDF[viewing.id]}
                          title="Download this set as AI-enhanced PDF"
                        >
                          <Icon name="Download" size={13} /> {(downloadingPDF[set?.id] || downloadingPDF[viewing.id]) ? 'AI Formatting…' : 'AI PDF'}
                        </button>
                      </div>
                    </div>
                    {(!set.questions || set.questions.length === 0) ? (
                      <div className="sfp-q-empty">No questions in this set.</div>
                    ) : (
                      set.questions.map((q, qi) => (
                        <div className="sfp-q" key={qi}>
                          <div className="sfp-q-top">
                            <span className="sfp-q-num">{q.number || `Q${qi + 1}`}{q.question_type ? ` · ${q.question_type}` : ''}</span>
                            {q.marks != null && <span className="sfp-q-marks">{q.marks} marks{q.difficulty ? ` · ${q.difficulty}` : ''}</span>}
                          </div>
                          <div className="sfp-q-text">{q.question_text || '—'}</div>
                        </div>
                      ))
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Encryption Modal */}
      {showEncryptionModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000
        }} onClick={() => setShowEncryptionModal(false)}>
          <div style={{
            background: 'var(--bg-primary)', borderRadius: '0.5rem', padding: '2rem',
            maxWidth: '400px', width: '90%', boxShadow: 'var(--shadow-xl)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>Optional Encryption</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Add a password to encrypt the PDF (minimum 6 characters, or leave blank for no encryption):
            </p>
            <input
              type="password"
              placeholder="Enter password (optional)"
              value={encryptionPassword}
              onChange={(e) => setEncryptionPassword(e.target.value)}
              style={{
                width: '100%', padding: '0.75rem', border: '1px solid var(--border-color)',
                borderRadius: '0.25rem', marginBottom: '1rem', boxSizing: 'border-box',
                background: 'var(--bg-secondary)', color: 'var(--text-primary)'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowEncryptionModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={confirmDownloadWithEncryption}>Download</button>
            </div>
          </div>
        </div>
      )}

      {/* Download Progress Modal */}
      {showDownloadProgressModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10001
        }}>
          <div style={{
            background: 'var(--bg-primary)', borderRadius: '0.5rem', padding: '2rem',
            textAlign: 'center', boxShadow: 'var(--shadow-xl)'
          }}>
            <div className="sfp-spin-wrap" style={{ padding: 0, marginBottom: '1rem' }}><div className="sfp-spinner" /></div>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: 'var(--text-primary)' }}>AI is formatting your paper…</p>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>This may take a few seconds</p>
          </div>
        </div>
      )}
    </div>
  );
}
